import { TextInput } from '@mantine/core';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { isEqual } from 'lodash';
import { IFormInputSpecFieldUpdatedAt } from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo, useRef } from 'react';

/**
 * Field that stores the last timestamp when another field changes.
 */
export default function RevisionFieldFieldUpdatedAt({
  spec,
  name,
  deviceId,
}: RevisionFieldProps<IFormInputSpecFieldUpdatedAt>) {
  const { form, findInputByName } = useFillOutRevisionDataProvider();
  const sourceInput = useMemo(() => findInputByName(spec.sourceField), [findInputByName, spec.sourceField]);

  const sourceValue = useMemo(() => {
    if (!spec.sourceField) {
      return undefined;
    }

    if (sourceInput?.context === 'revision') {
      return form.values.fields[spec.sourceField];
    }

    if (sourceInput?.context === 'device' && deviceId !== undefined) {
      return form.values.devices[String(deviceId)]?.fields?.[spec.sourceField];
    }

    return undefined;
  }, [form.values, spec.sourceField, sourceInput?.context, deviceId]);

  const previousSourceValue = useRef<unknown>(sourceValue);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      previousSourceValue.current = sourceValue;
      return;
    }

    if (isEqual(previousSourceValue.current, sourceValue)) {
      return;
    }

    previousSourceValue.current = sourceValue;
    form.setFieldValue(name, new Date().toLocaleString('sk'));
  }, [sourceValue, form, name]);

  const props = form.getInputProps(name);

  return <TextInput size="lg" label={spec.label} required={spec.required} readOnly {...props} />;
}
